/*
 * Settings, read first and edit second: each card shows what is set, with its own Edit. Sections a role cannot use are
 * left out. Values a location did not set itself come from the brand or HQ, and say so.
 */
import { Link, useNavigate } from "@tanstack/react-router";
import {
  addDays,
  BRAND_LINKS,
  formatMoney,
  KEY_PROVIDERS,
  keyMistake,
  keyProvidersAt,
  oneLine,
  SETTINGS_DEFAULTS,
  subLocationName,
  TRADE_LOOKS,
  weekOf,
  type Cents,
  type CompanyLevel,
  type KeyProvider,
  type Settings as SettingsShape,
} from "@steward/shared";
import { shortDate, todayIn } from "../../components/format";
import { useState, type ReactNode } from "react";
import { api, ApiError } from "../../api/client";
import {
  useAudit,
  useEndSession,
  useKeys,
  useOutbox,
  useRemoveKey,
  useSaveKey,
  useSessions,
  useSettings,
  useUpdateCompany,
  useUpdateSettings,
  useUploadLogo,
  type OutboxItem,
} from "../../api/hooks";
import { useSession } from "../../app/session";
import { AddressFields, draftOf, partsOf, SubLocationSheet, subLocationLine } from "../../components/SubLocations";
import { DataTable } from "../../components/DataTable";
import { Icon } from "../../components/Icon";
import { Choice, Combobox, InputText } from "../../components/fields";
import { Modal, useToast } from "../../components/overlays";
import { ago, Banner, Button, Card, Details, Page, SettingRow, StatusLabel, Switch, type Status } from "../../components/parts";
import { useQueryClient } from "@tanstack/react-query";

const errorText = (e: unknown) => (e instanceof ApiError ? e.message : "That didn't save. Try again.");

const ZONES = [
  ["America/Chicago", "Central (New Orleans, Chicago)"],
  ["America/New_York", "Eastern (New York, Atlanta)"],
  ["America/Denver", "Mountain (Denver)"],
  ["America/Phoenix", "Arizona (Phoenix)"],
  ["America/Los_Angeles", "Pacific (Los Angeles)"],
  ["America/Anchorage", "Alaska"],
  ["Pacific/Honolulu", "Hawaii"],
] as const;
const zoneName = (tz: string) => ZONES.find(([z]) => z === tz)?.[1] ?? tz;

/**
 * A location (or franchise) can't change its own name or phone; the brand or HQ sets them. An owner of a company above it
 * still can (the server checks the same rule).
 */
function useNameLocked(): boolean {
  const { company, companies } = useSession();
  if (company.level !== "location") return false;
  for (let up = company.parentId; up;) {
    const above = companies.find((c) => c.id === up);
    if (!above) break;
    if (above.role === "owner") return false;
    up = above.parentId;
  }
  return true;
}

/** Head office sets a company's web address: an owner above it, or the top company for itself (the server checks too). */
function useAddressOpen(): boolean {
  const { company, companies } = useSession();
  if (!company.parentId) return company.role === "owner";
  for (let up: string | null = company.parentId; up;) {
    const above = companies.find((c) => c.id === up);
    if (!above) break;
    if (above.role === "owner") return true;
    up = above.parentId;
  }
  return false;
}

export function BusinessInfo() {
  const { company, companies, can } = useSession();
  const [editing, setEditing] = useState<"info" | "brand" | null>(null);
  const editable = can("company.update");
  const locked = useNameLocked();
  // Brand color belongs to the brand (a company with locations below it); locations take it from there.
  const isBrand = companies.some((c) => c.parentId === company.id) || company.level !== "location";
  const edit = (what: "info" | "brand") =>
    editable ? (
      <Button type="secondary" size="small" onClick={() => setEditing(what)}>
        Edit
      </Button>
    ) : null;
  return (
    <>
      <Card id="info" title="Business info" action={edit("info")} flush>
        <Details
          rows={[
            ["Name", company.name],
            ["Phone", company.phone],
            ["Email", company.email],
            ["Address", oneLine(companyParts(company)) || null],
            ["Time zone", zoneName(company.timezone)],
          ]}
        />
        {locked && editable ? (
          <p className="muted small card-note">
            The brand sets the name, phone and email for every location. Ask them to change it.
          </p>
        ) : null}
      </Card>
      {isBrand ? (
        <Card id="brand" title="Brand" action={edit("brand")} flush>
          <Details
            rows={[
              ...BRAND_COLORS.map(([key, label]): [string, ReactNode] => {
                const c = company[key];
                return [
                  label,
                  c ? (
                    <>
                      <span className="swatch" style={{ background: c }} />
                      {c.toUpperCase()}
                    </>
                  ) : key === "brandColor" ? null : (
                    <span className="muted">{builtIn(company.trade, key)} built in</span>
                  ),
                ];
              }),
              ["Web address", company.appUrl ?? `${company.linkUrl} (from above)`],
              [
                "Client web address",
                company.clientUrl ??
                  (company.clientLinkUrl === company.linkUrl ? "Same as web address" : `${company.clientLinkUrl} (from above)`),
              ],
              [
                "Logo",
                company.logoUrl ? (
                  <span className="logo-on-brand" style={{ background: "#fff" }}>
                    <img src={company.logoUrl} alt="" />
                  </span>
                ) : null,
              ],
              [
                "White logo",
                company.logoWhiteUrl ? (
                  <span
                    className="logo-on-brand"
                    style={{ background: company.accentColor ?? builtIn(company.trade, "accentColor") }}
                  >
                    <img src={company.logoWhiteUrl} alt="" />
                  </span>
                ) : null,
              ],
            ]}
          />
          <p className="muted" style={{ padding: "0 16px 16px" }}>
            Every location of {company.name} uses these. The accent, secondary and call to action colors and the white logo are
            what clients see on pay and quote pages, emails and invoices. Pay links, quotes and the client hub open at the web
            address.
          </p>
        </Card>
      ) : null}
      {company.level === "location" ? <SubLocationsCard canEdit={!locked && editable} /> : null}
      <BrandLinksCard />
      {!editable ? <Banner>Only the owner can change business info. Ask the owner if something is wrong.</Banner> : null}
      {editing === "info" ? <BusinessEdit onClose={() => setEditing(null)} /> : null}
      {editing === "brand" ? <BrandEdit onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function BusinessEdit({ onClose }: { onClose: () => void }) {
  const { company } = useSession();
  const locked = useNameLocked();
  const save = useUpdateCompany();
  const toast = useToast();
  const [f, setF] = useState({
    name: company.name,
    phone: company.phone ?? "",
    email: company.email ?? "",
    timezone: company.timezone,
  });
  const [where, setWhere] = useState(draftOf(companyParts(company)));
  const [error, setError] = useState<string | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });
  const submit = () => {
    setError(null);
    const { parts, problem } = partsOf(where);
    if (problem) {
      setError(problem);
      return;
    }
    save.mutate(
      {
        id: company.id,
        patch: {
          // A location's name, phone and email are the brand's to change, so they are never sent from here.
          ...(locked ? {} : { name: f.name, phone: f.phone || null, email: f.email || null }),
          // The address in parts, like a client's (owner rule, 2026-11-02).
          address: parts?.street1 ?? null,
          address2: parts?.street2 ?? null,
          city: parts?.city ?? null,
          state: parts?.state ?? null,
          zip: parts?.zip ?? null,
          timezone: f.timezone,
        },
      },
      {
        onSuccess: () => {
          toast("Business info saved");
          onClose();
        },
        onError: (e) => setError(errorText(e)),
      },
    );
  };
  return (
    <Modal
      title="Edit business info"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button loading={save.isPending} onClick={submit}>
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <InputText
        label="Name"
        value={f.name}
        onChange={set("name")}
        required={!locked}
        disabled={locked}
        description={locked ? "The brand sets this for every location." : undefined}
      />
      <div className="form-grid">
        <InputText
          label="Phone"
          type="tel"
          value={f.phone}
          onChange={set("phone")}
          disabled={locked}
          description={locked ? "The brand sets this too." : undefined}
        />
        <InputText
          label="Email"
          type="email"
          value={f.email}
          onChange={set("email")}
          disabled={locked}
          description={locked ? "The brand sets this too." : undefined}
        />
      </div>
      <AddressFields value={where} onChange={setWhere} />
      <Combobox
        label="Time zone"
        value={f.timezone}
        onChange={(v) => setF({ ...f, timezone: v })}
        options={ZONES.map(([value, label]) => ({ value, label }))}
        description="Quiet hours and 'today' use this."
      />
    </Modal>
  );
}

/** A company's address parts (address is Street 1). */
function companyParts(c: {
  address: string | null;
  address2: string | null;
  city: string | null;
  state: string | null;
  zip: string | null;
}) {
  return {
    street1: c.address ?? "",
    ...(c.address2 ? { street2: c.address2 } : {}),
    city: c.city ?? "",
    state: c.state ?? "",
    zip: c.zip ?? "",
  };
}

/**
 * A location's sub locations (real branches like The Cleaners of Metairie), so everyone working here knows their name,
 * phone, email, address and ZIPs. The brand or head office sets them; an owner above can change them from here too.
 * The location's own people and its franchisee only see them.
 */
function SubLocationsCard({ canEdit }: { canEdit: boolean }) {
  const { company, companies } = useSession();
  const s = useSettings(company.id);
  const [open, setOpen] = useState<number | null>(null);
  const subs = s.data?.settings.messaging.locationLines ?? [];
  let brand = company.name;
  for (let up = company.parentId; up;) {
    const above = companies.find((c) => c.id === up);
    if (!above) break;
    if (above.level === "brand") brand = above.name;
    up = above.parentId;
  }
  return (
    <Card
      id="sub-locations"
      title="Sub locations"
      action={
        canEdit ? (
          <Button type="secondary" size="small" onClick={() => setOpen(-1)}>
            Add
          </Button>
        ) : null
      }
      flush
    >
      {subs.length ? (
        <Details
          rows={subs.map((x, i): [string, ReactNode] => [
            subLocationName(brand, x.name),
            canEdit ? (
              <button type="button" className="link" onClick={() => setOpen(i)}>
                {subLocationLine(x)}
              </button>
            ) : (
              subLocationLine(x)
            ),
          ])}
        />
      ) : (
        <p className="muted card-note">No sub locations.</p>
      )}
      {!canEdit ? (
        <p className="muted small card-note">The brand or head office sets sub locations. Ask them to change one.</p>
      ) : null}
      {open !== null ? (
        <SubLocationSheet
          location={{ id: company.id, name: company.name }}
          brand={brand}
          subs={subs}
          index={open}
          onClose={() => setOpen(null)}
        />
      ) : null}
    </Card>
  );
}

/**
 * The brand kit's colors, in the order Settings shows them. Only the brand color is a must. The other three change what
 * clients see (lookFor in @steward/shared client-look.ts); each left empty uses the trade's built-in one.
 */
const BRAND_COLORS = [
  ["brandColor", "Brand color", "The app's own buttons and the band on the sign in page."],
  ["accentColor", "Accent color", "The top band and links on pay and quote pages, emails and invoices."],
  ["secondaryColor", "Secondary color", "The bottom band on pay and quote pages, emails and invoices."],
  ["ctaColor", "Call to action color", "The main button clients press, like Pay and Approve."],
] as const;
type BrandColorKey = (typeof BRAND_COLORS)[number][0];

/** The trade's own color for a part of the kit, used when the brand leaves it empty. */
function builtIn(trade: string, key: Exclude<BrandColorKey, "brandColor">): string {
  const look = TRADE_LOOKS[(trade in TRADE_LOOKS ? trade : "hq") as keyof typeof TRADE_LOOKS];
  return { accentColor: look.accent, secondaryColor: look.menu, ctaColor: look.cta }[key];
}

const isHex = (c: string) => /^#[0-9a-f]{6}$/i.test(c);

/** A logo box: drop a picture on it or click to pick one. It saves the moment it is picked. */
function LogoDrop({
  label,
  hint,
  url,
  onBrand,
  busy,
  onFile,
  onRemove,
}: {
  label: string;
  hint: string;
  url: string;
  /** The white logo is shown on the brand color so it can be seen. */
  onBrand?: string;
  busy: boolean;
  onFile: (f: File | undefined) => void;
  onRemove: () => void;
}) {
  const [over, setOver] = useState(false);
  return (
    <div className="logo-pick">
      <b>{label}</b>
      <label
        className={`logo-drop${over ? " over" : ""}`}
        // The color logo is shown on white, as clients see it, even in dark mode.
        style={onBrand ? { background: onBrand } : url ? { background: "#fff" } : undefined}
        onDragOver={(e) => {
          e.preventDefault();
          setOver(true);
        }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setOver(false);
          if (!busy) onFile(e.dataTransfer.files[0]);
        }}
      >
        {url ? (
          <img src={url} alt={label} />
        ) : (
          <span className={onBrand ? "on-brand" : "muted"}>
            {busy ? "Uploading..." : "Drop a logo here or click to pick one"}
          </span>
        )}
        <input
          type="file"
          accept="image/png,image/jpeg,image/webp"
          hidden
          disabled={busy}
          onChange={(e) => {
            onFile(e.target.files?.[0]);
            e.target.value = "";
          }}
        />
      </label>
      <p className="muted small">
        {hint}
        {url ? (
          <>
            {" "}
            <button type="button" className="link" onClick={onRemove}>
              Remove
            </button>
          </>
        ) : null}
      </p>
    </div>
  );
}

function BrandEdit({ onClose }: { onClose: () => void }) {
  const { company } = useSession();
  const save = useUpdateCompany();
  const toast = useToast();
  const addressOpen = useAddressOpen();
  const upload = useUploadLogo();
  const [colors, setColors] = useState<Record<BrandColorKey, string>>({
    brandColor: company.brandColor ?? "#0B4F55",
    ctaColor: company.ctaColor ?? "",
    secondaryColor: company.secondaryColor ?? "",
    accentColor: company.accentColor ?? "",
  });
  const setColor = (k: BrandColorKey, v: string) => setColors((c) => ({ ...c, [k]: v }));
  // A logo saves the moment it is picked; Save here only sends it when it was removed.
  const [logo, setLogo] = useState(company.logoUrl ?? "");
  const [white, setWhite] = useState(company.logoWhiteUrl ?? "");
  const [saved, setSaved] = useState({ color: company.logoUrl ?? "", white: company.logoWhiteUrl ?? "" });
  const [uploading, setUploading] = useState<"color" | "white" | null>(null);
  const [address, setAddress] = useState(company.appUrl ?? "");
  const [clientAddress, setClientAddress] = useState(company.clientUrl ?? "");
  const [error, setError] = useState<string | null>(null);
  const pickLogo = async (variant: "color" | "white", f: File | undefined) => {
    if (!f) return;
    setError(null);
    if (!["image/png", "image/jpeg", "image/webp"].includes(f.type)) {
      setError("Use a PNG, JPG or WebP picture.");
      return;
    }
    if (f.size > 2 * 1_048_576) {
      setError("That picture is too big. The most is 2 MB.");
      return;
    }
    const dataBase64 = await new Promise<string>((resolve, reject) => {
      const r = new FileReader();
      r.onload = () => resolve(typeof r.result === "string" ? (r.result.split(",")[1] ?? "") : "");
      r.onerror = () => reject(new Error("That picture could not be read."));
      r.readAsDataURL(f);
    });
    setUploading(variant);
    upload.mutate(
      { name: f.name, mime: f.type, dataBase64, variant },
      {
        onSuccess: (c) => {
          if (variant === "white") setWhite(c.logoWhiteUrl ?? "");
          else setLogo(c.logoUrl ?? "");
          setSaved({ color: c.logoUrl ?? "", white: c.logoWhiteUrl ?? "" });
          toast(variant === "white" ? "White logo saved" : "Logo saved");
        },
        onError: (e) => setError(errorText(e)),
        onSettled: () => setUploading(null),
      },
    );
  };
  const badColor = BRAND_COLORS.find(([k]) => colors[k] !== "" && !isHex(colors[k]));
  return (
    <Modal
      title="Edit brand"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            disabled={uploading !== null}
            onClick={() => {
              if (badColor) {
                setError(`Check the ${badColor[1].toLowerCase()}. Use a color like #1F6F5C.`);
                return;
              }
              save.mutate(
                {
                  id: company.id,
                  patch: {
                    brandColor: colors.brandColor,
                    ctaColor: colors.ctaColor || null,
                    secondaryColor: colors.secondaryColor || null,
                    accentColor: colors.accentColor || null,
                    ...(logo !== saved.color ? { logoUrl: logo || null } : {}),
                    ...(white !== saved.white ? { logoWhiteUrl: white || null } : {}),
                    // Only sent by head office, so a brand owner saving their color never trips the address lock.
                    ...(addressOpen ? { appUrl: address.trim() || null, clientUrl: clientAddress.trim() || null } : {}),
                  },
                },
                {
                  onSuccess: () => {
                    toast("Brand saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              );
            }}
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <div className="brand-colors">
        {BRAND_COLORS.map(([key, label, about]) => (
          <InputText
            key={key}
            label={label}
            value={colors[key]}
            onChange={(e) => setColor(key, e.target.value)}
            placeholder={key === "brandColor" ? undefined : `${builtIn(company.trade, key)} built in`}
            description={about}
            prefix={
              <input
                type="color"
                aria-label={`Pick the ${label.toLowerCase()}`}
                value={isHex(colors[key]) ? colors[key] : key === "brandColor" ? "#000000" : builtIn(company.trade, key)}
                onChange={(e) => setColor(key, e.target.value)}
              />
            }
          />
        ))}
      </div>
      <div className="logo-pair">
        <LogoDrop
          label="Logo"
          hint="For white backgrounds. Used on the color band only when there is no white logo."
          url={logo}
          busy={uploading === "color"}
          onFile={(f) => void pickLogo("color", f)}
          onRemove={() => setLogo("")}
        />
        <LogoDrop
          label="White logo"
          hint="All white. Sits on the top band of pay and quote pages, emails, invoices and the sign in page."
          url={white}
          onBrand={isHex(colors.accentColor) ? colors.accentColor : builtIn(company.trade, "accentColor")}
          busy={uploading === "white"}
          onFile={(f) => void pickLogo("white", f)}
          onRemove={() => setWhite("")}
        />
      </div>
      <p className="muted small">
        PNG, JPG or WebP, up to 2 MB each. A wide logo on a clear background looks best. Both are used at every location of the
        brand.
      </p>
      <InputText
        label="Web address"
        value={address}
        onChange={(e) => setAddress(e.target.value)}
        placeholder={company.linkUrl}
        disabled={!addressOpen}
        description={
          addressOpen
            ? "Where this brand's office and crew sign in. Leave it empty to use the one above."
            : "Head office sets this."
        }
      />
      <InputText
        label="Client web address"
        value={clientAddress}
        onChange={(e) => setClientAddress(e.target.value)}
        placeholder={company.clientLinkUrl}
        disabled={!addressOpen}
        description={
          addressOpen
            ? "Where clients open pay links, quotes, reviews and their hub. Leave it empty to use the web address."
            : "Head office sets this."
        }
      />
    </Modal>
  );
}

export const WEEKDAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];

export function MoneySettings() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const [editing, setEditing] = useState<"invoices" | "print" | null>(null);
  if (!s.data) return <Card title="Invoices">Loading</Card>;
  const { settings, own } = s.data;
  const ownInv = (own.invoices ?? {}) as Record<string, unknown>;
  const from = (set: Record<string, unknown>, key: string) => (key in set ? null : <span className="muted"> · from above</span>);
  const edit = (k: "invoices" | "print") =>
    can("settings.update") ? (
      <Button type="secondary" size="small" onClick={() => setEditing(k)}>
        Edit
      </Button>
    ) : null;
  return (
    <>
      <Card id="invoices" title="Invoices and payments" action={edit("invoices")} flush>
        <Details
          rows={[
            [
              "Email subject",
              <>
                {settings.invoices.subject}
                {from(ownInv, "subject")}
              </>,
            ],
            [
              "Message",
              <>
                {settings.invoices.text}
                {from(ownInv, "text")}
              </>,
            ],
            [
              "Email words",
              <>
                {settings.invoices.email || "Built in words for your trade"}
                {from(ownInv, "email")}
              </>,
            ],
            [
              "Tips on the pay page",
              <>
                {settings.invoices.tips ? `On, ${settings.invoices.tipPercents.map((p) => `${String(p)}%`).join(", ")}` : "Off"}
                {from(ownInv, "tips")}
              </>,
            ],
            [
              "Ways to pay",
              <>
                {[
                  settings.invoices.payWays.card && "Card, Apple Pay, Google Pay",
                  settings.invoices.payWays.bank && "Bank",
                  settings.invoices.payWays.klarna &&
                    `Klarna on one-time work over ${formatMoney(settings.invoices.klarnaMinCents as Cents)}`,
                ]
                  .filter(Boolean)
                  .join(", ")}
                {from(ownInv, "payWays")}
              </>,
            ],
            [
              "Visit photos on the pay page",
              <>
                {settings.invoices.photosOnPayPage ? "Shown" : "Off"}
                {from(ownInv, "photosOnPayPage")}
              </>,
            ],
            [
              "Saved cards charged by themselves",
              <>
                {AUTO_CHARGE_WORDS[settings.invoices.autoChargeWhen]}
                {from(ownInv, "autoChargeWhen")}
              </>,
            ],
            [
              "Card saved or paid with",
              <>
                {AUTO_ON_SAVE_WORDS[settings.invoices.autoChargeOnSave]}
                {from(ownInv, "autoChargeOnSave")}
              </>,
            ],
            [
              "Homes past due after",
              <>
                {settings.invoices.pastDueHoursHome} hours{from(ownInv, "pastDueHoursHome")}
              </>,
            ],
            [
              "Businesses past due after",
              <>
                {settings.invoices.pastDueDaysBusiness} days{from(ownInv, "pastDueDaysBusiness")}
              </>,
            ],
          ]}
        />
      </Card>
      <Card id="on-invoice" title="On every invoice" action={edit("print")} flush>
        <Details
          rows={[
            [
              "Cash or check note",
              settings.invoices.payNote ? (
                <>
                  {settings.invoices.payNote}
                  {from(ownInv, "payNote")}
                </>
              ) : null,
            ],
            [
              "Small print",
              settings.invoices.footer ? (
                <>
                  {settings.invoices.footer}
                  {from(ownInv, "footer")}
                </>
              ) : null,
            ],
            [
              "Bill part of a job early",
              <>
                {settings.invoices.progress ? "On, from a one-time visit's More menu" : "Off"}
                {from(ownInv, "progress")}
              </>,
            ],
          ]}
        />
      </Card>
      <p className="muted">
        "From above" means this location uses what the brand or head office set. Changing it here sets it for this location only.
      </p>
      {editing === "invoices" ? <InvoiceEdit settings={settings} onClose={() => setEditing(null)} /> : null}
      {editing === "print" ? <PrintEdit settings={settings} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

function InvoiceEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [v, setV] = useState(settings.invoices);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit invoice settings"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { invoices: v },
                {
                  onSuccess: () => {
                    toast("Invoice settings saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <InputText
        label="Email subject"
        value={v.subject}
        onChange={(e) => setV({ ...v, subject: e.target.value })}
        description="{brand} becomes your business name."
      />
      <InputText
        label="Message"
        value={v.text}
        onChange={(e) => setV({ ...v, text: e.target.value })}
        description="{first} becomes the client's first name, {job} the job's name."
      />
      <label className="field wide">
        <span className="label">Email words</span>
        <textarea
          rows={5}
          maxLength={3000}
          value={v.email}
          placeholder="Leave empty to use the built in words for your trade."
          onChange={(e) => setV({ ...v, email: e.target.value })}
        />
        <span className="description">
          Shows above the amount and the Pay invoice button. {"{first}"} becomes the client's first name, {"{job}"} the job's
          name. Your name and phone go at the bottom by themselves.
        </span>
      </label>
      <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border)" }}>
        <SettingRow
          title="Tips on the pay page"
          changed={v.tips !== SETTINGS_DEFAULTS.invoices.tips}
          description="10%, 15%, 20% or another amount. Nothing picked for them."
          control={<Switch label="Tips" checked={v.tips} onChange={(x) => setV({ ...v, tips: x })} />}
        />
        <SettingRow
          title="Visit photos on the pay page"
          changed={v.photosOnPayPage !== SETTINGS_DEFAULTS.invoices.photosOnPayPage}
          description="Proof the work was done, like a mowed yard. Leave off for cleaning."
          control={<Switch label="Photos" checked={v.photosOnPayPage} onChange={(x) => setV({ ...v, photosOnPayPage: x })} />}
        />
        <SettingRow
          title="Card, Apple Pay, Google Pay"
          description="Always on"
          control={<Switch label="Card" checked disabled onChange={() => undefined} />}
        />
        <SettingRow
          title="Bank payment"
          changed={v.payWays.bank !== SETTINGS_DEFAULTS.invoices.payWays.bank}
          control={
            <Switch label="Bank" checked={v.payWays.bank} onChange={(x) => setV({ ...v, payWays: { ...v.payWays, bank: x } })} />
          }
        />
        <SettingRow
          title="Klarna"
          changed={v.payWays.klarna !== SETTINGS_DEFAULTS.invoices.payWays.klarna}
          control={
            <Switch
              label="Klarna"
              checked={v.payWays.klarna}
              onChange={(x) => setV({ ...v, payWays: { ...v.payWays, klarna: x } })}
            />
          }
        />
      </div>
      {v.payWays.klarna ? (
        <InputText
          label="Klarna shows on one-time work over"
          type="number"
          min={35}
          prefix="$"
          value={v.klarnaMinCents / 100}
          onChange={(e) => setV({ ...v, klarnaMinCents: Math.round(Number(e.target.value) * 100) })}
          description="Klarna is pay in 4. It never shows on repeating work or for clients charged by themselves."
        />
      ) : null}
      <Choice
        label="Charge saved cards by themselves"
        value={v.autoChargeWhen}
        onChange={(x) => setV({ ...v, autoChargeWhen: x })}
        options={(["now", "hour", "evening"] as const).map((x) => ({ value: x, label: AUTO_CHARGE_WORDS[x] }))}
        description="For repeating jobs whose client agreed to auto charge. A later time gives you room to fix the price or press Hold."
      />
      <Choice
        label="When a client saves or pays with a card"
        value={v.autoChargeOnSave}
        onChange={(x) => setV({ ...v, autoChargeOnSave: x })}
        options={(["on", "ask"] as const).map((x) => ({ value: x, label: AUTO_ON_SAVE_WORDS[x] }))}
        description="Clients on a repeating job agree under the Card terms. Ask shows a box on the pay page they tick first."
      />
      {v.tips ? (
        <div className="form-grid">
          {v.tipPercents.map((p, n) => (
            <InputText
              key={n}
              label={`Tip button ${String(n + 1)}`}
              type="number"
              min={1}
              max={50}
              suffix="%"
              value={p}
              onChange={(e) => setV({ ...v, tipPercents: v.tipPercents.map((x, k) => (k === n ? Number(e.target.value) : x)) })}
            />
          ))}
        </div>
      ) : null}
      <div className="form-grid">
        <InputText
          label="Homes past due after"
          type="number"
          min={0}
          suffix="hours"
          value={v.pastDueHoursHome}
          onChange={(e) => setV({ ...v, pastDueHoursHome: Number(e.target.value) })}
        />
        <InputText
          label="Businesses past due after"
          type="number"
          min={0}
          suffix="days"
          value={v.pastDueDaysBusiness}
          onChange={(e) => setV({ ...v, pastDueDaysBusiness: Number(e.target.value) })}
        />
      </div>
    </Modal>
  );
}

/** The words on every invoice: the cash or check note, the small print, and billing part of a job early. */
function PrintEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const [v, setV] = useState({
    payNote: settings.invoices.payNote,
    footer: settings.invoices.footer,
    progress: settings.invoices.progress,
  });
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit what every invoice says"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                { invoices: v },
                {
                  onSuccess: () => {
                    toast("Invoice words saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <label className="field wide">
        <span className="label">Cash or check note</span>
        <textarea
          rows={3}
          maxLength={500}
          value={v.payNote}
          placeholder="Make checks out to The Cleaners. Leave cash on the kitchen counter."
          onChange={(e) => setV({ ...v, payNote: e.target.value })}
        />
        <span className="description">Shows under the pay button and on the PDF while money is owed.</span>
      </label>
      <label className="field wide">
        <span className="label">Small print</span>
        <textarea
          rows={4}
          maxLength={1500}
          value={v.footer}
          placeholder="Late fees, cancel rules or anything your lawyer wants on every invoice."
          onChange={(e) => setV({ ...v, footer: e.target.value })}
        />
        <span className="description">Shows at the bottom of the pay page and the PDF.</span>
      </label>
      <div className="card" style={{ boxShadow: "none", border: "1px solid var(--color-border)" }}>
        <SettingRow
          title="Bill part of a job early"
          changed={v.progress !== SETTINGS_DEFAULTS.invoices.progress}
          description="A one-time visit's More menu gets Bill part now, for a deposit or a big job paid in steps."
          control={<Switch label="Bill part of a job early" checked={v.progress} onChange={(x) => setV({ ...v, progress: x })} />}
        />
      </div>
    </Modal>
  );
}

/** The brand's links at the bottom of the pay and quote pages (old Steward "Social networks"). */
function BrandLinksCard() {
  const { company, can } = useSession();
  const s = useSettings(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const [draft, setDraft] = useState<SettingsShape["brand"]["links"] | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (!s.data) return null;
  const links = s.data.settings.brand.links;
  const owner = can("settings.update");
  const bad = draft ? BRAND_LINKS.filter(([k]) => draft[k].trim() && !/^https:\/\/\S+$/.test(draft[k].trim())) : [];
  return (
    <Card
      id="links"
      title="Links on client pages"
      action={
        owner && !draft ? (
          <Button type="secondary" size="small" icon="edit" onClick={() => setDraft({ ...links })}>
            Edit
          </Button>
        ) : null
      }
      flush={!draft}
    >
      {draft ? (
        <div className="stack">
          {error ? <Banner type="error">{error}</Banner> : null}
          <div className="form-grid">
            {BRAND_LINKS.map(([k, label]) => (
              <InputText
                key={k}
                label={label}
                type="url"
                placeholder="https://"
                value={draft[k]}
                error={bad.some(([b]) => b === k) ? "Start with https://" : null}
                onChange={(e) => setDraft({ ...draft, [k]: e.target.value })}
              />
            ))}
          </div>
          <div className="row end">
            <Button type="tertiary" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={bad.length > 0}
              onClick={() =>
                save.mutate(
                  { brand: { links: Object.fromEntries(BRAND_LINKS.map(([k]) => [k, draft[k].trim()])) } },
                  {
                    onSuccess: () => {
                      setDraft(null);
                      toast("Links saved");
                    },
                    onError: (e) => setError(errorText(e)),
                  },
                )
              }
            >
              Save
            </Button>
          </div>
        </div>
      ) : (
        <>
          <Details rows={BRAND_LINKS.map(([k, label]) => [label, links[k] || null])} />
          <p className="muted small card-note">Clients see the filled ones at the bottom of pay and quote pages.</p>
        </>
      )}
    </Card>
  );
}

const AUTO_CHARGE_WORDS = { now: "Right after the visit", hour: "An hour after the visit", evening: "At 6 PM that day" } as const;
const AUTO_ON_SAVE_WORDS = { on: "Charge after each visit", ask: "Ask them first" } as const;

export function PayrollEdit({ settings, onClose }: { settings: SettingsShape; onClose: () => void }) {
  const save = useUpdateSettings();
  const toast = useToast();
  const { company } = useSession();
  const [day, setDay] = useState(String(settings.payroll.paydayWeekday));
  const [pool, setPool] = useState(
    settings.payroll.defaultPoolPercent === null ? "" : String(settings.payroll.defaultPoolPercent),
  );
  const [business, setBusiness] = useState(
    settings.payroll.commercialPoolPercent === null ? "" : String(settings.payroll.commercialPoolPercent),
  );
  const [every, setEvery] = useState(String(settings.payroll.periodWeeks));
  // Two-week pay starts on a Monday after the last paid week; offer this Monday and the next five.
  const monday = weekOf(todayIn(company.timezone));
  const mondays = [...new Set([settings.payroll.twoWeekFrom, ...[0, 1, 2, 3, 4, 5].map((n) => addDays(monday, n * 7))])]
    .filter((d): d is string => d !== null)
    .sort();
  const [firstMonday, setFirstMonday] = useState(settings.payroll.twoWeekFrom ?? monday);
  const [error, setError] = useState<string | null>(null);
  return (
    <Modal
      title="Edit payroll settings"
      open
      onClose={onClose}
      footer={
        <>
          <Button type="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            loading={save.isPending}
            onClick={() =>
              save.mutate(
                {
                  payroll: {
                    paydayWeekday: Number(day),
                    defaultPoolPercent: pool.trim() === "" ? null : Number(pool),
                    commercialPoolPercent: business.trim() === "" ? null : Number(business),
                    ...(every === "2" ? { periodWeeks: 2 as const, twoWeekFrom: firstMonday } : { periodWeeks: 1 as const }),
                  },
                },
                {
                  onSuccess: () => {
                    toast("Payroll settings saved");
                    onClose();
                  },
                  onError: (e) => setError(errorText(e)),
                },
              )
            }
          >
            Save
          </Button>
        </>
      }
    >
      {error ? <Banner type="error">{error}</Banner> : null}
      <Choice
        label="Pay every"
        value={every}
        onChange={setEvery}
        options={[
          { value: "1", label: "Week" },
          { value: "2", label: "Two weeks" },
        ]}
        description="Weekly is the usual. Paid weeks never change, so two-week pay starts after the last paid week."
      />
      {every === "2" ? (
        <Choice
          label="First two weeks start on"
          value={firstMonday}
          onChange={setFirstMonday}
          options={mondays.map((d) => ({ value: d, label: `Monday, ${shortDate(d)}` }))}
          description="Weeks before this day stay weekly."
        />
      ) : null}
      <Choice
        label="Payday"
        value={day}
        onChange={setDay}
        options={WEEKDAYS.slice(0, 5).map((d, i) => ({ value: String(i + 1), label: d }))}
        description="Each pay period is paid on this day after it ends."
      />
      <InputText
        label="Default pool"
        type="number"
        min={0}
        max={100}
        suffix="%"
        value={pool}
        onChange={(e) => setPool(e.target.value)}
        description="Used when nobody on a visit has a higher percent. Leave empty for none."
      />
      <InputText
        label="Business jobs pool"
        type="number"
        min={0}
        max={100}
        suffix="%"
        value={business}
        onChange={(e) => setBusiness(e.target.value)}
        description="Jobs for a business client pay this pool, whatever the crew's own percent. Leave empty to pay them like any job."
      />
    </Modal>
  );
}

const TEMPLATE_WORDS: Record<string, string> = {
  sign_in_code: "Sign-in code",
  invite: "Team invite",
  reminder: "Visit reminder",
  reply: "Reply",
  manual: "Message",
  invoice: "Invoice",
  campaign: "Campaign",
  "auto.review_request": "Review request",
  "alert.rating": "Low rating alert",
};
const STATUS_WORDS: Record<string, [Status, string]> = {
  dry_run: ["informative", "Test mode, not sent"],
  held: ["warning", "Waiting for 8 AM"],
  queued: ["warning", "Waiting to send"],
  sent: ["success", "Sent"],
  failed: ["critical", "Failed"],
  cancelled: ["inactive", "Stopped, they opted out"],
};

export function MessagesSettings() {
  const { company, role } = useSession();
  const s = useSettings(company.id);
  const box = useOutbox(company.id);
  const save = useUpdateSettings();
  const toast = useToast();
  const live = s.data?.settings.messaging.live ?? false;
  return (
    <>
      <Card id="sending" title="Sending" flush>
        <SettingRow
          title="Real texts, emails and card charges"
          description="Off means messages are saved below but nothing goes to clients or crew, and only Stripe test keys work. Only the owner can turn it on, once the server is set to go live."
          control={
            <Switch
              label="Real texts, emails and card charges"
              checked={live}
              disabled={role !== "owner"}
              onChange={(v) =>
                save.mutate(
                  { messaging: { live: v } },
                  {
                    onSuccess: () => toast(v ? "Real texts, emails and card charges are on" : "Real sending is off"),
                    onError: (e) => toast(errorText(e)),
                  },
                )
              }
            />
          }
        />
        <SettingRow
          title="Automatic messages"
          description="Reminders, follow-ups and receipts each have their own switch."
          control={
            <Link to="/settings/automations" className="btn tertiary small">
              Open
            </Link>
          }
        />
        <SettingRow
          title="Quiet hours"
          description="Texts wait from 8 PM to 8 AM, marketing texts from 7 PM to 9 AM. Emails go any time."
          control={<span className="muted">Always on</span>}
        />
      </Card>
      <DataTable<OutboxItem>
        title="Message log"
        rows={box.data?.items ?? []}
        rowKey={(m) => m.id}
        search={(m) => `${m.to} ${TEMPLATE_WORDS[m.template] ?? m.template}`}
        empty="No texts or emails yet. Invites and sign-in codes show here."
        columns={[
          {
            key: "when",
            header: "When",
            sort: (m) => -Date.parse(m.createdAt),
            render: (m) => <span className="num">{ago(m.createdAt)}</span>,
          },
          { key: "what", header: "What", render: (m) => <b>{TEMPLATE_WORDS[m.template] ?? m.template}</b> },
          { key: "to", header: "To", render: (m) => <span className="num">{m.to}</span> },
          { key: "how", header: "By", render: (m) => (m.channel === "sms" ? "Text" : m.channel === "email" ? "Email" : "Push") },
          {
            key: "status",
            header: "Status",
            render: (m) => {
              const [st, word] = STATUS_WORDS[m.status] ?? ["inactive", m.status];
              return <StatusLabel status={st}>{word}</StatusLabel>;
            },
          },
        ]}
      />
    </>
  );
}

// Each key's row name inside its company card and what it is for; the start of the key is named so a wrong paste shows
const PROVIDER_WORDS: Record<string, [string, string]> = {
  google_client_id: [
    "Sign-in client ID",
    "Lets each company press Sign in with Google on Marketing and pick its profile and Google Ads account. It ends in .apps.googleusercontent.com.",
  ],
  google_client_secret: ["Sign-in client secret", "The secret of the same sign-in client. It starts with GOCSPX-."],
  google: ["Server key", "Address lookup, routes and Google profile ratings. It starts with AIza."],
  google_maps: [
    "Maps key",
    "The maps on Home and the Schedule. It starts with AIza. Pages see it, so in Google lock it to our web addresses.",
  ],
  google_ads_token: [
    "Ads developer token (optional)",
    "Not needed now. Google gives Ads access through the Steward Cloud project. Only for an older token from Google Ads API Center.",
  ],
  google_service: ["Service account (backup)", "Only needed when nobody has signed in with Google. Paste the whole JSON file."],
  meta_app_id: [
    "App ID",
    "Lets each company press Sign in with Facebook on Marketing and pick its Page and ad account. In the app under App settings, Basic.",
  ],
  meta_app_secret: ["App secret", "On the same page as the App ID. Press Show to copy it."],
  meta: [
    "System user token (backup)",
    "Posts and ad spend for our own brands when nobody has signed in with Facebook. From Business Settings, System users.",
  ],
  quo: ["API key", "From Quo settings, API."],
  email: ["API key", "It starts with re_. Add and check each company's domain in Resend first."],
  anthropic: ["API key", "It starts with sk-ant-."],
  plaid_client_id: ["Client ID", "Only the owner types it in."],
  plaid_secret: ["Secret", "The secret of the same Plaid app."],
  fee_stripe: ["Secret key", "It starts with sk_ or rk_."],
  fee_stripe_pk: ["Publishable key", "It starts with pk_. The card fields a franchise owner saves the card with."],
  stripe: ["Secret key", "It starts with sk_ or rk_."],
  stripe_publishable: ["Publishable key", "It starts with pk_. Our pay page uses it for the card fields."],
  stripe_webhook: ["Webhook secret", "The signing secret of the webhook Stripe sends payments to. It starts with whsec_."],
};

// Keys grouped by the company that makes them, so each card says what that service runs (owner, 2026-11-02)
const KEY_GROUPS: { title: string; what: string; keys: string[] }[] = [
  {
    title: "Google",
    what: "Maps, address lookup, routes, your Google profile and reviews, Search Console and Google Ads.",
    keys: ["google_client_id", "google_client_secret", "google", "google_maps", "google_ads_token", "google_service"],
  },
  {
    title: "Meta",
    what: "Facebook and Instagram posts, Meta ad spend and leads.",
    keys: ["meta_app_id", "meta_app_secret", "meta"],
  },
  { title: "Quo", what: "Business texts and calls for every company.", keys: ["quo"] },
  { title: "Resend", what: "Invoices, receipts and invites by email, from each company's own address.", keys: ["email"] },
  { title: "Claude", what: "Eunice, the text receptionist, and Ask the Steward in Insights.", keys: ["anthropic"] },
  {
    title: "Plaid",
    what: "Links banks to Books, read only. Bank passwords never reach us.",
    keys: ["plaid_client_id", "plaid_secret"],
  },
  {
    title: "Stripe for franchise fees",
    what: "The Trades' own Stripe. Franchises pay the monthly fee and add-ons here.",
    keys: ["fee_stripe", "fee_stripe_pk"],
  },
  {
    title: "Stripe",
    what: "Card payments. Each company uses its own account, so money goes to that business.",
    keys: ["stripe", "stripe_publishable", "stripe_webhook"],
  },
];

const isKeyProvider = (p: string): p is KeyProvider => (KEY_PROVIDERS as readonly string[]).includes(p);

/** The cards a company at this level sees: only the keys it saves, and no empty cards. */
function keyGroupsAt(level: CompanyLevel) {
  const mine: readonly string[] = keyProvidersAt(level);
  return KEY_GROUPS.map((g) => ({ ...g, keys: g.keys.filter((k) => mine.includes(k)) })).filter((g) => g.keys.length > 0);
}

/** "Google sign-in client secret", "Meta app ID": the card name and the row name together, for the key window. */
function keyTitle(p: string): string {
  const group = KEY_GROUPS.find((g) => g.keys.includes(p))?.title;
  const name = PROVIDER_WORDS[p]?.[0] ?? p;
  return group ? `${group} ${name.charAt(0).toLowerCase()}${name.slice(1)}` : name;
}

// Where each key is made or changed, so the arrow beside a service opens that page (2026-10-29)
const KEY_PAGES: Record<string, string> = {
  stripe: "https://dashboard.stripe.com/apikeys",
  stripe_webhook: "https://dashboard.stripe.com/webhooks",
  stripe_publishable: "https://dashboard.stripe.com/apikeys",
  quo: "https://my.quo.com/settings/api",
  email: "https://resend.com/api-keys",
  google: "https://console.cloud.google.com/apis/credentials",
  google_maps: "https://console.cloud.google.com/google/maps-apis/credentials",
  anthropic: "https://platform.claude.com/settings/keys",
  plaid_client_id: "https://dashboard.plaid.com/developers/keys",
  plaid_secret: "https://dashboard.plaid.com/developers/keys",
  fee_stripe: "https://dashboard.stripe.com/apikeys",
  fee_stripe_pk: "https://dashboard.stripe.com/apikeys",
  meta: "https://business.facebook.com/settings/system-users",
  google_service: "https://console.cloud.google.com/iam-admin/serviceaccounts",
  google_ads_token: "https://console.cloud.google.com/apis/api/googleads.googleapis.com",
  google_client_id: "https://console.cloud.google.com/apis/credentials",
  google_client_secret: "https://console.cloud.google.com/apis/credentials",
  meta_app_id: "https://developers.facebook.com/apps",
  meta_app_secret: "https://developers.facebook.com/apps",
};

export function KeysSettings() {
  const { company } = useSession();
  const keys = useKeys(company.id);
  const save = useSaveKey();
  const remove = useRemoveKey();
  const toast = useToast();
  const [adding, setAdding] = useState<string | null>(null);
  const [confirming, setConfirming] = useState<string | null>(null);
  const [secret, setSecret] = useState("");
  const [error, setError] = useState<string | null>(null);
  const mistake = adding && isKeyProvider(adding) ? keyMistake(adding, secret) : null;
  return (
    <>
      <Banner>Keys are stored locked (encrypted). After you save one, only its first 4 characters ever show again.</Banner>
      {keyGroupsAt(company.level).map((g, i) => (
        <Card key={g.title} id={i === 0 ? "keys" : `keys-${g.keys[0]}`} title={g.title} flush>
          <p className="muted small card-note">{g.what}</p>
          {g.keys.map((p) => {
            const k = keys.data?.find((x) => x.provider === p);
            const [name, what] = PROVIDER_WORDS[p] ?? [p, ""];
            const page = KEY_PAGES[p];
            return (
              <SettingRow
                key={p}
                title={
                  <>
                    {name}{" "}
                    {k ? (
                      <StatusLabel status="success">Saved · {k.preview}</StatusLabel>
                    ) : (
                      <StatusLabel status="inactive">Not set</StatusLabel>
                    )}
                  </>
                }
                description={
                  <>
                    {what}
                    {page ? (
                      <a
                        className="key-page"
                        href={page}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={`Open ${keyTitle(p)} where the key lives`}
                        aria-label={`Open ${keyTitle(p)} where the key lives`}
                      >
                        <Icon name="arrowUpRight" size={14} />
                      </a>
                    ) : null}
                  </>
                }
                control={
                  <div className="row" style={{ flexWrap: "nowrap" }}>
                    {k ? (
                      <Button
                        type="tertiary"
                        variation="destructive"
                        size="small"
                        onBlur={() => setConfirming(null)}
                        onClick={() => {
                          if (confirming !== p) {
                            setConfirming(p);
                            return;
                          }
                          setConfirming(null);
                          remove.mutate(p, { onSuccess: () => toast(`${keyTitle(p)} removed`) });
                        }}
                      >
                        {confirming === p ? "Remove?" : "Remove"}
                      </Button>
                    ) : null}
                    <Button
                      type="secondary"
                      size="small"
                      onClick={() => {
                        setAdding(p);
                        setSecret("");
                        setError(null);
                      }}
                    >
                      {k ? "Replace" : "Add key"}
                    </Button>
                  </div>
                }
              />
            );
          })}
        </Card>
      ))}
      <p className="muted small">
        {company.level === "hq"
          ? "These run for every company. Each brand and location saves its own Stripe."
          : "Head office runs texts, emails, maps and AI for every company, so those keys are set there. Ask head office if one needs to change."}
      </p>
      <Modal
        title={adding ? keyTitle(adding) : ""}
        open={adding !== null}
        onClose={() => setAdding(null)}
        footer={
          <>
            <Button type="tertiary" onClick={() => setAdding(null)}>
              Cancel
            </Button>
            <Button
              loading={save.isPending}
              disabled={secret.trim().length < 8 || !!mistake}
              onClick={() =>
                adding &&
                save.mutate(
                  { provider: adding, secret },
                  {
                    onSuccess: () => {
                      toast("Key saved");
                      setAdding(null);
                    },
                    onError: (e) => setError(errorText(e)),
                  },
                )
              }
            >
              Save key
            </Button>
          </>
        }
      >
        {error ? <Banner type="error">{error}</Banner> : null}
        {mistake ? <Banner type="warning">{mistake}</Banner> : null}
        {adding === "stripe" ? (
          <Banner type="warning">
            A test key (sk_test) works now. A live key (sk_live) is used only once the owner turns on real texts, emails and card
            charges for this brand.
          </Banner>
        ) : null}
        <InputText
          label="Secret key"
          type="text"
          className="secret"
          autoComplete="off"
          spellCheck={false}
          data-1p-ignore
          data-lpignore="true"
          data-bwignore
          data-form-type="other"
          value={secret}
          onChange={(e) => setSecret(e.target.value)}
          description="Paste it from the service's settings page."
        />
      </Modal>
    </>
  );
}

function deviceName(ua: string | null, kind: string): string {
  if (kind === "mobile") return "Steward phone app";
  if (kind === "claude") return ua ?? "Claude";
  if (!ua) return "Web browser";
  const browser = ua.includes("Edg/")
    ? "Edge"
    : ua.includes("Chrome/")
      ? "Chrome"
      : ua.includes("Firefox/")
        ? "Firefox"
        : ua.includes("Safari/")
          ? "Safari"
          : "Web browser";
  const os = /iPhone|iPad/.test(ua)
    ? "iPhone"
    : ua.includes("Android")
      ? "Android"
      : ua.includes("Mac OS X")
        ? "Mac"
        : ua.includes("Windows")
          ? "Windows"
          : "";
  return os ? `${browser} on ${os}` : browser;
}

/** Backup codes for when the phone is lost. Logins moved from the old Steward start with none. */
function BackupCodesRow({ left }: { left: number }) {
  const qc = useQueryClient();
  const [codes, setCodes] = useState<string[] | null>(null);
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);
  const make = async () => {
    setBusy(true);
    try {
      setCodes((await api<{ backupCodes: string[] }>("POST", "auth/two-step/backup-codes")).backupCodes);
      await qc.invalidateQueries({ queryKey: ["me"] });
    } finally {
      setBusy(false);
      setConfirm(false);
    }
  };
  return (
    <>
      <SettingRow
        title="Backup codes"
        description={
          left === 0
            ? "You have none. Make some now, so a lost phone can't lock you out."
            : `${String(left)} left. Each one works once if you lose your phone. Making new ones stops the old ones.`
        }
        control={
          <Button
            type={left === 0 ? "primary" : "secondary"}
            size="small"
            loading={busy}
            onBlur={() => setConfirm(false)}
            onClick={() => (left === 0 || confirm ? void make() : setConfirm(true))}
          >
            {confirm ? "Stop the old codes?" : "Make new codes"}
          </Button>
        }
      />
      {codes ? (
        <div className="setting-row">
          <Banner type="warning">
            Save these now, somewhere other than this phone. They won&apos;t show again.
            <pre className="num">{codes.join("\n")}</pre>
          </Banner>
        </div>
      ) : null}
    </>
  );
}

export function SecuritySettings() {
  const { me, role, can } = useSession();
  const sessions = useSessions();
  const end = useEndSession();
  const toast = useToast();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [confirm, setConfirm] = useState(false);
  const everywhere = async () => {
    await api("POST", "auth/sign-out-everywhere");
    qc.clear();
    await navigate({ to: "/sign-in" });
  };
  const inner = (
    <>
      <Card id="two-step" title="Two-step sign in" flush>
        <SettingRow
          title={me.twoStepOn ? "On" : "Off"}
          description={
            me.twoStepOn
              ? "You type a code from your authenticator app after your password. Lost your phone? Use one of your backup codes."
              : role === "crew"
                ? "You sign in with a texted code, so you don't need it."
                : "Owners and managers must turn it on."
          }
          control={
            me.twoStepOn ? (
              <StatusLabel status="success" pill>
                On
              </StatusLabel>
            ) : (
              <StatusLabel status="inactive" pill>
                Off
              </StatusLabel>
            )
          }
        />
        {me.twoStepOn ? <BackupCodesRow left={me.backupCodesLeft} /> : null}
      </Card>
      <DataTable
        title="Signed-in devices"
        rows={sessions.data ?? []}
        rowKey={(s) => s.id}
        tools={
          <Button
            type="secondary"
            variation="destructive"
            size="small"
            onBlur={() => setConfirm(false)}
            onClick={() => (confirm ? void everywhere() : setConfirm(true))}
          >
            {confirm ? "Sign out everywhere?" : "Sign out everywhere"}
          </Button>
        }
        columns={[
          {
            key: "device",
            header: "Device",
            render: (s) => (
              <div className="name">
                <b>{deviceName(s.userAgent, s.kind)}</b>
                {s.current ? <span className="muted"> · This device</span> : null}
              </div>
            ),
          },
          { key: "where", header: "Address", render: (s) => <span className="num muted">{s.ip ?? "Unknown"}</span> },
          { key: "used", header: "Last used", render: (s) => ago(s.lastUsedAt) },
          {
            key: "end",
            header: "",
            align: "right",
            render: (s) =>
              s.current ? null : (
                <Button
                  type="tertiary"
                  size="small"
                  onClick={() => end.mutate(s.id, { onSuccess: () => toast("Device signed out") })}
                >
                  Sign out
                </Button>
              ),
          },
        ]}
      />
      {can("audit.read") ? <ActivitySettings /> : null}
    </>
  );
  return role === "crew" ? <Page title="My devices">{inner}</Page> : inner;
}

const ACTION_WORDS: Record<string, string> = {
  "auth.sign_in": "Signed in",
  "auth.sign_in_failed": "Wrong password typed",
  "auth.two_step_on": "Turned on two-step",
  "auth.backup_code_used": "Used a backup code",
  "people.invite": "Sent an invite",
  "people.invite_accepted": "Accepted an invite",
  "people.update": "Changed a person",
  "settings.update": "Changed settings",
  "company.create": "Added a company",
  "company.update": "Changed business info",
  "keys.save": "Saved a service key",
  "keys.remove": "Removed a service key",
};

export function ActivitySettings() {
  const { company } = useSession();
  const audit = useAudit(company.id);
  return (
    <DataTable
      title="Activity"
      rows={audit.data?.items ?? []}
      rowKey={(a) => a.id}
      search={(a) => `${a.userName ?? ""} ${ACTION_WORDS[a.action] ?? a.action}`}
      empty="Sign-ins, invites and changes to settings show here."
      columns={[
        { key: "when", header: "When", render: (a) => <span className="num">{ago(a.createdAt)}</span> },
        { key: "who", header: "Who", render: (a) => a.userName ?? "The system" },
        { key: "what", header: "What", render: (a) => <b>{ACTION_WORDS[a.action] ?? a.action}</b> },
        { key: "from", header: "From", render: (a) => <span className="num muted">{a.ip ?? ""}</span> },
      ]}
    />
  );
}
